import { useEffect, useRef, useState } from 'react';
import { useDataProvider } from 'react-admin';
import {
  ActivityLog,
  ActivityQuery,
  ActivityScanProgress,
  ActivityStopReason,
  ScanStatus,
} from './types';

// Progress is additive API metadata: older backends can omit it during rollout.
const readProgress = (value: any): ActivityScanProgress | null => {
  const counters = (item: any) =>
    item &&
    ['pagesFetched', 'emptyPages', 'replayedPages', 'candidateChecks'].every(
      (key) => Number.isSafeInteger(item[key]) && item[key] >= 0
    );
  return counters(value) &&
    value.groups &&
    typeof value.groups === 'object' &&
    !Array.isArray(value.groups) &&
    Object.keys(value.groups).every(
      (key) =>
        counters(value.groups[key]) &&
        typeof value.groups[key].exhausted === 'boolean' &&
        typeof value.groups[key].candidateReady === 'boolean'
    )
    ? value
    : null;
};

const wait = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve) => {
    const finish = () => {
      window.clearTimeout(timer);
      signal.removeEventListener('abort', finish);
      resolve();
    };
    const timer = window.setTimeout(finish, ms);
    signal.addEventListener('abort', finish);
    if (signal.aborted) finish();
  });

export const useActivityLogs = (query: ActivityQuery, pageSize: number) => {
  const dataProvider = useDataProvider();
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [status, setStatus] = useState<ScanStatus>('loading');
  const [error, setError] = useState('');
  const [requiresRefresh, setRequiresRefresh] = useState(false);
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);
  const [target, setTarget] = useState(pageSize);
  const [progress, setProgress] = useState<ActivityScanProgress | null>(null);
  const [stopReason, setStopReason] = useState<ActivityStopReason | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [requestStartedAt, setRequestStartedAt] = useState<number | null>(null);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const current = useRef({
    generation: 0,
    controller: null as AbortController | null,
    logs: [] as ActivityLog[],
    cursor: null as string | null,
    exhausted: false,
    requiresRefresh: false,
    target: pageSize,
    elapsedMs: 0,
    activeSince: null as number | null,
  });

  const finishTiming = () => {
    const state = current.current;
    if (state.activeSince !== null) {
      state.elapsedMs += Math.max(0, Date.now() - state.activeSince);
      state.activeSince = null;
    }
    return state.elapsedMs;
  };

  const stop = () => {
    finishTiming();
    current.current.generation++;
    current.current.controller?.abort();
    current.current.controller = null;
  };

  const run = async (reset: boolean, nextTarget: number) => {
    if (!reset && current.current.requiresRefresh) return;
    stop();
    const state = current.current;
    const generation = state.generation;
    const controller = new AbortController();
    state.controller = controller;
    state.target = nextTarget;
    setTarget(nextTarget);
    if (reset) {
      state.logs = [];
      state.cursor = null;
      state.exhausted = false;
      state.requiresRefresh = false;
      setLogs([]);
      setRequiresRefresh(false);
      setFetchedAt(null);
      setProgress(null);
      setStopReason(null);
      state.elapsedMs = 0;
    }
    state.activeSince = Date.now();
    setElapsedMs(state.elapsedMs);
    setRequestStartedAt(null);
    setRetryAttempt(0);
    setStatus('loading');
    setError('');
    let retries = 0;
    const active = () => !controller.signal.aborted && generation === state.generation;
    try {
      while (active() && state.logs.length < nextTarget && !state.exhausted) {
        try {
          setRequestStartedAt(Date.now());
          const { data } = await dataProvider.get('activityLogs', {
            ...query,
            pageSize: Math.min(100, nextTarget - state.logs.length),
            cursor: state.cursor,
            signal: controller.signal,
          });
          if (!active()) return;
          if (
            !Array.isArray(data?.items) ||
            typeof data.exhausted !== 'boolean' ||
            (!data.exhausted && typeof data.cursor !== 'string')
          ) {
            throw new Error('Unexpected activity log response');
          }
          // A retry can replay a response; row identities include the severity group.
          const seen = new Set(state.logs.map(({ id }) => id));
          const incoming = data.items.filter((item: ActivityLog) => {
            if (seen.has(item.id)) return false;
            seen.add(item.id);
            return true;
          });
          state.logs = [...state.logs, ...incoming];
          state.cursor = data.cursor;
          state.exhausted = data.exhausted;
          setLogs(state.logs);
          setFetchedAt(Date.now());
          // Replace cumulative snapshots rather than adding them: retries may replay a response.
          setProgress(readProgress(data.progress));
          setStopReason(
            ['PAGE_FILLED', 'EXHAUSTED', 'DEADLINE', 'AWS_TIMEOUT'].includes(data.stopReason)
              ? data.stopReason
              : null
          );
          setRetryAttempt(0);
          setRequestStartedAt(null);
          retries = 0;
          if (!data.exhausted && state.logs.length < nextTarget) await wait(250, controller.signal);
        } catch (failure) {
          if (!active()) return;
          const retryable =
            [429, 502, 503, 504].includes(failure.status) || failure instanceof TypeError;
          if (!retryable || retries >= 3) throw failure;
          setRetryAttempt(retries + 1);
          setRequestStartedAt(null);
          await wait(500 * Math.pow(2, retries++), controller.signal);
        }
      }
      if (active()) setStatus(state.exhausted ? 'complete' : 'full');
    } catch (failure) {
      if (active()) {
        state.requiresRefresh = failure.status === 400;
        setRequiresRefresh(state.requiresRefresh);
        setError(
          failure.message ||
            (state.requiresRefresh
              ? 'Review the filters and refresh to start a new search.'
              : 'Could not load activity logs. Retry or refresh the search.')
        );
        setStatus('error');
      }
    } finally {
      if (active()) {
        setElapsedMs(finishTiming());
        setRequestStartedAt(null);
        setRetryAttempt(0);
        state.controller = null;
      }
    }
  };

  // The serialized request deliberately freezes the date range throughout all slices.
  const queryKey = JSON.stringify(query);
  useEffect(() => {
    run(true, pageSize);
    return stop;
    // Only an applied filter/page-size change starts a new search.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey, pageSize]);

  useEffect(() => {
    if (status !== 'loading') return;
    const timer = window.setInterval(() => {
      const state = current.current;
      if (state.activeSince !== null) {
        setElapsedMs(state.elapsedMs + Math.max(0, Date.now() - state.activeSince));
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [status]);

  return {
    logs,
    status,
    error,
    requiresRefresh,
    fetchedAt,
    target,
    progress,
    stopReason,
    elapsedMs,
    requestStartedAt,
    retryAttempt,
    refresh: () => run(true, pageSize),
    cancel: () => {
      stop();
      setElapsedMs(current.current.elapsedMs);
      setRequestStartedAt(null);
      setRetryAttempt(0);
      setStatus('paused');
    },
    resume: () => run(false, current.current.target),
    loadMore: () => run(false, current.current.logs.length + pageSize),
  };
};
